import { Stack } from "@mui/material"
import Joi from "joi"
import { isEmpty, noop, omit, pick } from "lodash"
import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { UserThingTypeMrn } from "shared/userData"
import {
  useCreateThingsMutation,
  useUpdateThingsMutation,
} from "src/features/things/thingsApi"
import FormFields, { FormField } from "./FormFields"
import { useNotification } from "./NotificationProvider"
import { Sl2Btn } from "./Sl2Btn"
import { Sl2FormContainer } from "./Sl2FormContainer"

const joiSchema = Joi.object({
  firstName: Joi.string().required(),
  lastName: Joi.string().required(),
  email: Joi.string()
    .email({
      tlds: { allow: false },
    })
    .optional(),
  profilePictureUrl: Joi.string().uri().optional(),
  phoneNumber: Joi.string().pattern(/^[0-9]+$/),
  address: Joi.string().optional(),
  city: Joi.string().optional(),
  language: Joi.string().optional(),
  state: Joi.string().optional(),
  country: Joi.string().optional(),
  postalCode: Joi.string().optional(),
  // isSuperAdmin: Joi.boolean().optional(),
})

// Get valid field names from the schema
const validFields = [
  "firstName",
  "lastName",
  "email",
  "profilePictureUrl",
  "phoneNumber",
  "address",
  "city",
  "language",
  "state",
  "country",
  "postalCode",
  // "isSuperAdmin",
]

// Filter user object to only include valid fields
const filterUserFields = (user: Record<string, any>) => pick(user, validFields)

interface SaUserEditFormProps {
  user?: Record<string, any>
  superAdmin?: boolean
  onSave?: () => void
  onError?: (error: any) => void
  onCancel?: () => void
}

export const SaUserEditForm: React.FC<SaUserEditFormProps> = ({
  user = {},
  superAdmin = false,
  onSave = noop,
  onError = noop,
  onCancel = noop,
}) => {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const { showNotification } = useNotification()

  // Filter user object to only include valid fields
  const filteredUser = useMemo(() => filterUserFields(user), [user])

  const { fieldsGeneral, fieldsAddress } = useMemo(() => {
    const fieldsGeneral: FormField[] = [
      {
        name: "firstName",
        fieldType: "text",
        label: t("firstName"),
        required: true,
        size: "small",
        fullWidth: true,
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: "lastName",
        fieldType: "text",
        label: t("lastName"),
        required: true,
        size: "small",
        fullWidth: true,
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: "email",
        fieldType: "text",
        label: t("email"),
        required: false,

        size: "small",
        fullWidth: true,
        InputProps: {
          type: "email",
          readOnly: true,
        },
        gridProps: { xs: 12, sm: 12 },
      },
      {
        name: "phoneNumber",
        fieldType: "text",
        label: t("phoneNumber"),
        size: "small",
        fullWidth: true,
        InputProps: {
          type: "tel",
        },
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: "language",
        fieldType: "text",
        label: t("language"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 12, sm: 6 },
      },
      // {
      //   name: "isSuperAdmin",
      //   fieldType: "bool",
      //   label: t("isSuperAdmin"),
      //   size: "small",
      //   gridProps: { xs: 12 },

      // },
    ]

    const fieldsAddress: FormField[] = [
      {
        name: "address",
        fieldType: "text",
        label: t("address"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 12 },
      },
      {
        name: "city",
        fieldType: "text",
        label: t("city"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 6 },
      },
      {
        name: "state",
        fieldType: "text",
        label: t("state"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 6 },
      },
      {
        name: "postalCode",
        fieldType: "text",
        label: t("postalCode"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 6 },
      },
      {
        name: "country",
        fieldType: "text",
        label: t("country"),
        size: "small",
        fullWidth: true,
        gridProps: { xs: 6 },
      },
    ]

    return { fieldsGeneral, fieldsAddress }
  }, [t])

  const [createThings] = useCreateThingsMutation()
  const [updateThings] = useUpdateThingsMutation()

  const isCreateMode = useMemo(() => isEmpty(filteredUser), [filteredUser])
  const onSubmit = useCallback(
    async (data: any) => {
      try {
        setIsProcessing(true)
        const userData = {
          ...data,
          thingTypeMrn: UserThingTypeMrn,
          ...(isCreateMode && superAdmin ? { isSuperAdmin: true } : {}),
        }
        if (isCreateMode) {
          await createThings({
            body: { things: [userData] },
          }).unwrap()
          showNotification(t("userCreatedSuccessfully"), "success")
        } else {
          // For updates, we need to use update mutation
          const patch = omit(userData, ["email"])
          await updateThings({
            body: { things: [{ id: user.id, ...patch }] },
          }).unwrap()
          showNotification(t("userUpdatedSuccessfully"), "success")
        }
        onSave()
      } catch (e: any) {
        console.error(e)
        showNotification(
          isCreateMode ? t("userCreationFailed") : t("userUpdateFailed"),
          "error",
        )
        onError(e)
      } finally {
        setIsProcessing(false)
      }
    },
    [
      createThings,
      isCreateMode,
      superAdmin,
      onSave,
      onError,
      showNotification,
      updateThings,
      t,
    ],
  )

  return (
    <Sl2FormContainer
      onError={(errors: any) => console.error(errors)}
      onSuccess={onSubmit}
      joiSchema={joiSchema}
      defaultValues={filteredUser}
    >
      <FormFields title={t("general")} fields={fieldsGeneral} />
      <FormFields title={t("address")} fields={fieldsAddress} />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3 }}
      >
        <Sl2Btn onClick={onCancel} disabled={isProcessing} variant="outlined">
          {t("cancel")}
        </Sl2Btn>
        <Sl2Btn type="submit" disabled={isProcessing} variant="contained">
          {t(isCreateMode ? "create" : "save")}
        </Sl2Btn>
      </Stack>
    </Sl2FormContainer>
  )
}

export default SaUserEditForm
